// qs-brush-BowTie.ts
import type { KonvaLine } from '@/types/qs-type-konva'
import Konva from 'konva'
import { BaseBrush } from '../base/qs-brushes-base'
import { TaperingEngine } from '../engines/qs-brush-engine-tapering'
import type { BrushConfig, BrushContext, BrushDrawingResult, BrushProperties, BrushRecreationContext, BrushSegment } from '../qs-brushes-types'

export class BowTieBrush extends BaseBrush {
  readonly config: BrushConfig = {
    id: 'bow-tie',
    name: 'Bow-Tie Brush',
    category: 'tapered',
    description: 'Thick at ends, thin in middle - opposite of pointed brush',
    icon: '🎀',
    minSize: 1,
    maxSize: 100,
    defaultSize: 10
  }

  getProperties(size: number): BrushProperties {
    return {
      lineCap: 'round',
      lineJoin: 'round',
      dashPattern: [],
      strokeWidth: size,
      isTapered: true,
      originalStrokeWidth: size
    }
  }

  createBrushLine(context: BrushContext): KonvaLine {
    const properties = this.createBaseLineProperties(context)
    const pathId = `path-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

    return new Konva.Line({
      id: pathId,
      points: [context.position.x, context.position.y],
      ...properties,
      dash: Array.from(properties.dashPattern),
      isBowTieBrush: true,
      originalStrokeWidth: context.size
    })
  }

  continueDrawing(existingLine: KonvaLine, context: BrushContext): void {
    const currentPoints = existingLine.points()
    const newPoints = currentPoints.concat([context.position.x, context.position.y])
    existingLine.points(newPoints)
  }

  async finishDrawing(existingLine: KonvaLine, context: BrushContext): Promise<BrushDrawingResult> {
    const points = existingLine.points()
    const pathId = existingLine.id()

    if (points.length < 4) {
      return {
        konvaObjects: [existingLine],
        originalPath: this.createOriginalPath(points, context, pathId),
        pathData: {
          id: pathId,
          layerId: context.layerId,
          points: points,
          stroke: context.color,
          strokeWidth: context.size,
          lineCap: 'round',
          lineJoin: 'round',
          dash: [],
          opacity: context.opacity,
          timestamp: Date.now(),
          brushType: this.config.id,
          isTapered: false,
          isThickToThin: false,
          isThinToThick: false,
          isSpray: false,
          isDiagonal: false,
          segmentCount: 0,
          dotCount: 0,
          visualData: {
            segments: [],
            dots: []
          }
        }
      }
    }

    const taperingResult = await TaperingEngine.createTaperedLine({
      points: points,
      originalStrokeWidth: context.size,
      stroke: context.color,
      opacity: context.opacity,
      type: 'bow-tie',
      returnVisualData: true,
      smoothingMode: context.smoothingMode
    })

    taperingResult.group.id(pathId)

    const childrenCount = typeof taperingResult.group.children === 'function' ? taperingResult.group.children().length : 0
    if (childrenCount > 0 && TaperingEngine.canCacheGroup(taperingResult.group)) {
      taperingResult.group.cache()
    }

    const originalPath = this.createOriginalPath(points, context, pathId)

    const layer = existingLine.getLayer()
    if (layer) {
      existingLine.remove()
    }
    existingLine.destroy()

    const segmentCount = taperingResult.visualSegments?.length ?? (typeof taperingResult.group.children === 'function' ? taperingResult.group.children().length : 1)

    return {
      konvaObjects: [taperingResult.group],
      originalPath: originalPath,
      pathData: {
        id: pathId,
        layerId: context.layerId,
        points: points,
        stroke: context.color,
        strokeWidth: context.size,
        lineCap: 'round',
        lineJoin: 'round',
        dash: [],
        opacity: context.opacity,
        timestamp: Date.now(),
        brushType: this.config.id,
        isTapered: true,
        isThickToThin: false,
        isThinToThick: false,
        isSpray: false,
        isDiagonal: false,
        segmentCount: segmentCount,
        dotCount: 0,
        visualData: {
          segments: taperingResult.visualSegments ?? [],
          dots: []
        }
      }
    }
  }

  async recreateFromData(context: BrushRecreationContext): Promise<void> {
    const { pathData, konvaLayer } = context

    if (pathData.visualData?.segments && pathData.visualData.segments.length > 0) {
      const taperedGroup = new Konva.Group({
        id: pathData.id,
        draggable: false
      })

      pathData.visualData.segments.forEach((segmentData: BrushSegment) => {
        const segment = new Konva.Line({
          points: Array.from(segmentData.points),
          stroke: pathData.stroke,
          strokeWidth: segmentData.strokeWidth,
          opacity: segmentData.opacity,
          lineCap: 'round',
          lineJoin: 'round',
          draggable: false
        })
        taperedGroup.add(segment)
      })

      if (pathData.visualData.segments.length > 10 && TaperingEngine.canCacheGroup(taperedGroup)) {
        taperedGroup.cache()
        TaperingEngine.setupProgressiveSharpness(taperedGroup)
      }

      konvaLayer.add(taperedGroup)

      const originalPath = this.createOriginalPath(Array.from(pathData.points), {
        position: { x: 0, y: 0 },
        size: pathData.strokeWidth,
        color: pathData.stroke,
        opacity: pathData.opacity,
        layerId: ''
      }, pathData.id)

      konvaLayer.add(originalPath)
    } else {
      const taperingResult = await TaperingEngine.createTaperedLine({
        points: Array.from(pathData.points),
        originalStrokeWidth: pathData.strokeWidth,
        stroke: pathData.stroke,
        opacity: pathData.opacity,
        type: 'bow-tie',
        returnVisualData: false
      })

      taperingResult.group.id(pathData.id)

      const childrenCount = typeof taperingResult.group.children === 'function' ? taperingResult.group.children().length : 0
      if (childrenCount > 0 && TaperingEngine.canCacheGroup(taperingResult.group)) {
        taperingResult.group.cache()
        TaperingEngine.setupProgressiveSharpness(taperingResult.group)
      }

      konvaLayer.add(taperingResult.group)

      const originalPath = this.createOriginalPath(Array.from(pathData.points), {
        position: { x: 0, y: 0 },
        size: pathData.strokeWidth,
        color: pathData.stroke,
        opacity: pathData.opacity,
        layerId: ''
      }, pathData.id)

      konvaLayer.add(originalPath)
    }
  }
}
